import * as THREE from 'three';
import { STYLES, type StyleName } from './styles';

const EDGE_ANGLE = 25;

// EdgesGeometry is pure (geometry + angle) and immutable, but expensive to
// compute. Style switching previously rebuilt it for every mesh child on every
// call — the cause of the style-switch frame stall. Cache & share it; the
// per-mesh LineBasicMaterial stays fresh so the scenario dim path can still
// mutate edge color/opacity per object without bleeding across meshes.
const edgesCache = new Map<string, THREE.EdgesGeometry>();

function getEdges(geometry: THREE.BufferGeometry): THREE.EdgesGeometry {
  const key = `${geometry.uuid}|${EDGE_ANGLE}`;
  let edges = edgesCache.get(key);
  if (!edges) {
    edges = new THREE.EdgesGeometry(geometry, EDGE_ANGLE);
    edgesCache.set(key, edges);
  }
  return edges;
}

/**
 * Release cached edge geometry. Must be called on city teardown — otherwise
 * entries (keyed by geometry uuid) outlive disposed geometries and accumulate
 * across regenerations, since this Map is module-scoped.
 */
export function clearEdgesCache(): void {
  for (const edges of edgesCache.values()) edges.dispose();
  edgesCache.clear();
}

let currentStyle: StyleName = 'dark';

export function setCurrentStyle(style: StyleName): void {
  currentStyle = style;
}

export function getCurrentStyle(): StyleName {
  return currentStyle;
}

export function applyTronMaterial(object: THREE.Object3D, category: string): void {
  applyStyle(object, category, currentStyle);
}

export function applyStyle(object: THREE.Object3D, category: string, styleName: StyleName): void {
  const style = STYLES[styleName];

  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;

    const origColor = (child.material as THREE.MeshStandardMaterial)?.color?.getHex?.() ?? 0x333333;
    const result = style.material(origColor, category);

    child.material = new THREE.MeshPhongMaterial({
      color: result.color,
      emissive: result.emissive,
      emissiveIntensity: result.emissiveIntensity,
      shininess: result.shininess,
      transparent: result.opacity < 1,
      opacity: result.opacity,
      flatShading: styleName === 'light',
    });

    child.castShadow = styleName === 'light';
    child.receiveShadow = styleName === 'light';

    const existing = child.children.filter(c => c.userData.isEdgeLine);
    existing.forEach(c => child.remove(c));

    if (child.geometry) {
      const edges = getEdges(child.geometry);
      const lineMat = new THREE.LineBasicMaterial({ color: result.edgeColor, transparent: true, opacity: 1 });
      const line = new THREE.LineSegments(edges, lineMat);
      line.userData.isEdgeLine = true;
      child.add(line);
    }
  });
}
